import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  PlusOutlined,
  StarFilled,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Image,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import PageTitle from 'apps/home/components/PageTitle';
import fundProductsListApiCall from 'apps/investcore/apiCalls/fund-products/list';
import {
  createInvestmentThemeApiCall,
  createInvestmentThemeCategoryApiCall,
  deleteInvestmentThemeApiCall,
  deleteInvestmentThemeCategoryApiCall,
  getInvestmentThemeApiCall,
  listInvestmentThemeCategoriesApiCall,
  listInvestmentThemesApiCall,
  updateInvestmentThemeApiCall,
  updateInvestmentThemeCategoryApiCall,
} from 'apps/investcore/apiCalls/investment-themes';
import stockProductsApiCall from 'apps/investcore/apiCalls/stock-products';
import ErrorMessage from 'common/ui/ErrorMessage';
import LoadingSpinner from 'common/ui/LoadingSpinner';
import { useFetchApiList } from 'hooks/api';

const debounceTimeout = 500;

const ASSET_TYPE_OPTIONS = [
  { value: 'stock', label: 'Cổ phiếu & ETF' },
  { value: 'fund', label: 'Quỹ mở' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Nháp', color: 'default' },
  { value: 'published', label: 'Đã xuất bản', color: 'green' },
  { value: 'archived', label: 'Lưu trữ', color: 'orange' },
];

const CURATOR_OPTIONS = [
  { value: 'ops', label: 'Ops' },
  { value: 'agent', label: 'Agent' },
];

function assetTypeLabel(value) {
  return ASSET_TYPE_OPTIONS.find(o => o.value === value)?.label || value;
}

function StatusTag({ status }) {
  const option = STATUS_OPTIONS.find(o => o.value === status);
  return <Tag color={option?.color}>{option?.label || status}</Tag>;
}

function formatReturn(value) {
  if (value === null || value === undefined) return '—';
  const sign = value > 0 ? '+' : '';
  return (
    <span className={value >= 0 ? 'text-green-600' : 'text-red-500'}>
      {sign}
      {value.toFixed(2)}%
    </span>
  );
}

function useCategories() {
  const { data } = useQuery({
    queryKey: [listInvestmentThemeCategoriesApiCall.queryKey],
    queryFn: () => listInvestmentThemeCategoriesApiCall.queryFn(undefined),
  });
  return data?.data || [];
}

/* ---------------- Theme list ---------------- */

export const InvestmentThemeListPage = () => {
  const queryClient = useQueryClient();
  const categories = useCategories();
  const [filters, setFilters] = useState({
    status: undefined,
    asset_type: undefined,
    category_id: undefined,
    search: '',
  });
  const [search, setSearch] = useState('');

  const debouncedSearch = useMemo(
    () => debounce(value => setFilters(prev => ({ ...prev, search: value })), debounceTimeout),
    []
  );

  const { data: response, isLoading, error: fetchError } = useQuery({
    queryKey: [listInvestmentThemesApiCall.queryKey, filters],
    queryFn: () => listInvestmentThemesApiCall.queryFn(filters),
  });

  const { mutate: remove } = useMutation({
    mutationFn: id => deleteInvestmentThemeApiCall.queryFn({ id }),
    onSuccess: () => {
      message.success('Đã xoá chủ đề');
      queryClient.invalidateQueries({ queryKey: [listInvestmentThemesApiCall.queryKey] });
    },
    onError: err => {
      message.error(err?.message || 'Xoá chủ đề thất bại');
    },
  });

  if (fetchError) {
    return <ErrorMessage {...fetchError} />;
  }

  const themes = response?.data || [];

  const columns = [
    {
      title: 'Chủ đề',
      dataIndex: 'name',
      key: 'name',
      render: (value, record) => (
        <div>
          <Link to={`/admin/investment-themes/${record.id}/edit`} className="font-medium">
            {record.is_featured && <StarFilled className="mr-1 text-yellow-500" />}
            {value}
          </Link>
          <div className="text-xs text-gray-400">/{record.slug}</div>
        </div>
      ),
    },
    {
      title: 'Loại',
      dataIndex: 'asset_type',
      key: 'asset_type',
      render: value => assetTypeLabel(value),
    },
    {
      title: 'Nhóm',
      key: 'category',
      render: (_, record) => record.category?.name || '—',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: value => <StatusTag status={value} />,
    },
    { title: 'Số mã', dataIndex: 'item_count', key: 'item_count', align: 'right' as const },
    {
      title: '1T',
      key: 'return_1m',
      align: 'right' as const,
      render: (_, record) => formatReturn(record.performance?.return_1m),
    },
    {
      title: '1N',
      key: 'return_1y',
      align: 'right' as const,
      render: (_, record) => formatReturn(record.performance?.return_1y),
    },
    { title: 'Thứ tự', dataIndex: 'ordering', key: 'ordering', align: 'right' as const },
    {
      title: 'Tuyển chọn',
      dataIndex: 'curated_by',
      key: 'curated_by',
      render: value => (value === 'agent' ? <Tag color="purple">Agent</Tag> : <Tag>Ops</Tag>),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <Link to={`/admin/investment-themes/${record.id}/edit`}>Sửa</Link>
          <Popconfirm title="Xoá chủ đề này?" onConfirm={() => remove(record.id)}>
            <DeleteOutlined style={{ color: '#ff4d4f', cursor: 'pointer' }} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageTitle
        title="Investment Themes"
        backUrl="/admin"
        extra={
          <div className="flex gap-2">
            <Link to="/admin/investment-theme-categories">
              <Button>Nhóm chủ đề</Button>
            </Link>
            <Link to="/admin/investment-themes/create">
              <Button type="primary" icon={<PlusOutlined />}>
                Thêm chủ đề
              </Button>
            </Link>
          </div>
        }
      />
      <p className="mb-4 text-gray-500">
        Bộ sưu tập cổ phiếu/ETF hoặc quỹ mở theo chủ đề, hiển thị cho khách hàng trên app và web.
        Mỗi chủ đề chỉ chứa một loại tài sản.
      </p>

      <div className="mb-4 flex flex-wrap gap-2">
        <Input.Search
          allowClear
          placeholder="Tìm theo tên, slug"
          style={{ width: 240 }}
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            debouncedSearch(e.target.value);
          }}
        />
        <Select
          allowClear
          placeholder="Trạng thái"
          style={{ width: 160 }}
          options={STATUS_OPTIONS}
          value={filters.status}
          onChange={value => setFilters(prev => ({ ...prev, status: value }))}
        />
        <Select
          allowClear
          placeholder="Loại tài sản"
          style={{ width: 160 }}
          options={ASSET_TYPE_OPTIONS}
          value={filters.asset_type}
          onChange={value => setFilters(prev => ({ ...prev, asset_type: value }))}
        />
        <Select
          allowClear
          placeholder="Nhóm"
          style={{ width: 200 }}
          options={categories.map(c => ({ value: c.id, label: c.name }))}
          value={filters.category_id}
          onChange={value => setFilters(prev => ({ ...prev, category_id: value }))}
        />
      </div>

      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={themes}
        pagination={false}
      />
    </>
  );
};

/* ---------------- Theme form ---------------- */

const EMPTY_FORM = {
  name: '',
  name_en: '',
  slug: '',
  short_description: '',
  description: '',
  cover_image: '',
  icon: '',
  category_id: null,
  asset_type: 'stock',
  status: 'draft',
  ordering: 0,
  is_featured: false,
  curated_by: 'ops',
};

function toRow(item) {
  return {
    product_id: item.product_id,
    product_type: item.product_type,
    symbol: item.symbol,
    name: item.name,
    note: item.note || '',
    return_1m: item.return_1m,
    return_1y: item.return_1y,
  };
}

function Field({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className="mb-1 block text-sm text-gray-500">{label}</label>
      {children}
    </div>
  );
}

export const InvestmentThemeFormPage = () => {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const categories = useCategories();

  const { data: response, isLoading, error: fetchError } = useQuery({
    queryKey: [getInvestmentThemeApiCall.queryKey, id],
    queryFn: () => getInvestmentThemeApiCall.queryFn({ id }),
    enabled: !isNew,
  });

  const [form, setForm] = useState(EMPTY_FORM);
  const [rows, setRows] = useState([]);
  const [initialized, setInitialized] = useState(isNew);

  useEffect(() => {
    if (response?.data && !initialized) {
      const theme = response.data;
      setForm({
        name: theme.name || '',
        name_en: theme.name_en || '',
        slug: theme.slug || '',
        short_description: theme.short_description || '',
        description: theme.description || '',
        cover_image: theme.cover_image || '',
        icon: theme.icon || '',
        category_id: theme.category?.id || null,
        asset_type: theme.asset_type,
        status: theme.status,
        ordering: theme.ordering || 0,
        is_featured: !!theme.is_featured,
        curated_by: theme.curated_by || 'ops',
      });
      setRows((theme.items || []).map(toRow));
      setInitialized(true);
    }
  }, [response, initialized]);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const isFundTheme = form.asset_type === 'fund';

  const {
    data: stocks = [],
    load: loadStocks,
    isLoading: isLoadingStocks,
  } = useFetchApiList(stockProductsApiCall.list, { resourceName: 'data' });
  const {
    data: funds = [],
    load: loadFunds,
    isLoading: isLoadingFunds,
  } = useFetchApiList(fundProductsListApiCall, { resourceName: 'data' });

  // type: undefined overrides the picker's stock-only default so ETFs show
  // up too; closed-end funds listed in the stocks table are filtered below.
  const searchProducts = useMemo(
    () =>
      debounce((assetType, value) => {
        if (assetType === 'fund') {
          loadFunds({ search: value, page_size: 20 });
        } else {
          loadStocks({ type: undefined, search: value, page_size: 20 });
        }
      }, debounceTimeout),
    [loadStocks, loadFunds]
  );

  useEffect(() => {
    if (isFundTheme) {
      loadFunds({ page_size: 20 });
    } else {
      loadStocks({ type: undefined, page_size: 20 });
    }
  }, [isFundTheme]);

  const options = isFundTheme
    ? funds.map(f => ({
        value: f.id,
        symbol: f.alias || f.code,
        name: f.name,
        product_type: 'fund',
      }))
    : stocks
        .filter(s => s.type === 'stock' || s.type === 'etf')
        .map(s => ({ value: s.id, symbol: s.symbol, name: s.name, product_type: s.type }));
  const selectedIds = rows.map(r => r.product_id);
  const availableOptions = options.filter(o => !selectedIds.includes(o.value));

  const addProduct = productId => {
    const option = options.find(o => o.value === productId);
    if (!option) return;
    setRows(prev => [
      ...prev,
      {
        product_id: option.value,
        product_type: option.product_type,
        symbol: option.symbol,
        name: option.name,
        note: '',
      },
    ]);
  };

  const moveRow = (index, delta) => {
    setRows(prev => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const updateNote = (productId, note) => {
    setRows(prev => prev.map(r => (r.product_id === productId ? { ...r, note } : r)));
  };

  const removeRow = productId => {
    setRows(prev => prev.filter(r => r.product_id !== productId));
  };

  const changeAssetType = value => {
    if (value === form.asset_type) return;
    if (rows.length === 0) {
      setField('asset_type', value);
      return;
    }
    Modal.confirm({
      title: 'Đổi loại tài sản?',
      content: 'Một chủ đề chỉ chứa một loại tài sản. Danh sách sản phẩm hiện tại sẽ bị xoá.',
      okText: 'Đổi',
      cancelText: 'Huỷ',
      onOk: () => {
        setRows([]);
        setField('asset_type', value);
      },
    });
  };

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: () => {
      const payload = {
        ...form,
        items: rows.map(r => ({ product_id: r.product_id, note: r.note })),
      };
      return isNew
        ? createInvestmentThemeApiCall.queryFn(payload)
        : updateInvestmentThemeApiCall.queryFn({ id, ...payload });
    },
    onSuccess: () => {
      message.success('Đã lưu chủ đề');
      queryClient.invalidateQueries({ queryKey: [listInvestmentThemesApiCall.queryKey] });
      queryClient.invalidateQueries({ queryKey: [getInvestmentThemeApiCall.queryKey, id] });
      navigate('/admin/investment-themes');
    },
    onError: err => {
      message.error(err?.message || 'Lưu chủ đề thất bại');
    },
  });

  const { mutate: remove, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteInvestmentThemeApiCall.queryFn({ id }),
    onSuccess: () => {
      message.success('Đã xoá chủ đề');
      queryClient.invalidateQueries({ queryKey: [listInvestmentThemesApiCall.queryKey] });
      navigate('/admin/investment-themes');
    },
    onError: err => {
      message.error(err?.message || 'Xoá chủ đề thất bại');
    },
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (fetchError) {
    return <ErrorMessage {...fetchError} />;
  }

  const publishWithoutItems = form.status === 'published' && rows.length === 0;
  const canSave = !!form.name.trim() && !publishWithoutItems;

  return (
    <>
      <PageTitle
        title={isNew ? 'Thêm chủ đề đầu tư' : `Sửa chủ đề: ${form.name}`}
        backUrl="/admin/investment-themes"
      />

      <div className="my-4 grid max-w-3xl grid-cols-2 gap-4">
        <Field label="Tên chủ đề" className="col-span-2">
          <Input
            value={form.name}
            onChange={e => setField('name', e.target.value)}
            placeholder="VD: Ngân hàng dẫn đầu"
          />
        </Field>
        <Field label="Tên tiếng Anh">
          <Input value={form.name_en} onChange={e => setField('name_en', e.target.value)} />
        </Field>
        <Field label="Slug (để trống để tự tạo từ tên)">
          <Input
            value={form.slug}
            onChange={e => setField('slug', e.target.value)}
            placeholder="ngan-hang-dan-dau"
          />
        </Field>
        <Field label="Mô tả ngắn (hiển thị trên thẻ)" className="col-span-2">
          <Input
            value={form.short_description}
            maxLength={512}
            onChange={e => setField('short_description', e.target.value)}
          />
        </Field>
        <Field label="Mô tả chi tiết" className="col-span-2">
          <Input.TextArea
            rows={4}
            value={form.description}
            onChange={e => setField('description', e.target.value)}
          />
        </Field>
        <Field label="Ảnh bìa (URL)">
          <Input
            value={form.cover_image}
            onChange={e => setField('cover_image', e.target.value)}
            placeholder="https://img.stag.vn/..."
          />
          {form.cover_image && (
            <Image className="mt-2" height={80} src={form.cover_image} />
          )}
        </Field>
        <Field label="Icon (URL)">
          <Input
            value={form.icon}
            onChange={e => setField('icon', e.target.value)}
            placeholder="https://img.stag.vn/..."
          />
          {form.icon && <Image className="mt-2" height={40} src={form.icon} />}
        </Field>
        <Field label="Loại tài sản">
          <Select
            value={form.asset_type}
            onChange={changeAssetType}
            options={ASSET_TYPE_OPTIONS}
            style={{ width: '100%' }}
          />
        </Field>
        <Field label="Nhóm chủ đề">
          <Select
            allowClear
            value={form.category_id}
            onChange={value => setField('category_id', value || null)}
            options={categories.map(c => ({ value: c.id, label: c.name }))}
            placeholder="Không thuộc nhóm"
            style={{ width: '100%' }}
          />
        </Field>
        <Field label="Trạng thái">
          <Select
            value={form.status}
            onChange={value => setField('status', value)}
            options={STATUS_OPTIONS}
            style={{ width: '100%' }}
          />
        </Field>
        <Field label="Thứ tự hiển thị (nhỏ hiển thị trước)">
          <InputNumber
            value={form.ordering}
            onChange={value => setField('ordering', value || 0)}
            style={{ width: '100%' }}
          />
        </Field>
        <Field label="Tuyển chọn bởi">
          <Select
            value={form.curated_by}
            onChange={value => setField('curated_by', value)}
            options={CURATOR_OPTIONS}
            style={{ width: '100%' }}
          />
        </Field>
        <Field label="Nổi bật">
          <Switch checked={form.is_featured} onChange={value => setField('is_featured', value)} />
        </Field>
      </div>

      <h3 className="mt-6 text-base font-semibold">
        {isFundTheme ? 'Quỹ mở trong chủ đề' : 'Cổ phiếu & ETF trong chủ đề'}
      </h3>
      <p className="mb-2 text-sm text-gray-500">
        Thứ tự trong danh sách là thứ hạng hiển thị cho khách hàng.
      </p>
      <div className="my-2 flex items-center gap-2">
        <Select
          showSearch
          value={null}
          placeholder={isFundTheme ? 'Tìm quỹ để thêm' : 'Tìm mã cổ phiếu / ETF để thêm'}
          style={{ width: 360 }}
          filterOption={false}
          onSearch={value => searchProducts(form.asset_type, value)}
          onChange={addProduct}
          notFoundContent={isLoadingStocks || isLoadingFunds ? <Spin size="small" /> : null}
          options={availableOptions.map(o => ({
            value: o.value,
            label: `${o.symbol} - ${o.name}`,
          }))}
        />
      </div>

      {rows.length === 0 ? (
        <p className="py-6 text-center text-gray-500">Chưa có sản phẩm nào trong chủ đề.</p>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b text-left text-sm text-gray-500">
              <th className="w-10 py-2">#</th>
              <th className="py-2">Mã</th>
              <th className="py-2">Tên</th>
              <th className="py-2 text-right">1T</th>
              <th className="py-2 text-right">1N</th>
              <th className="py-2 pl-4">Ghi chú</th>
              <th className="w-28 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.product_id} className="border-b">
                <td className="py-2 text-gray-500">{index + 1}</td>
                <td className="py-2 font-medium">
                  {row.symbol}
                  {row.product_type === 'etf' && (
                    <Tag className="ml-2" color="blue">
                      ETF
                    </Tag>
                  )}
                </td>
                <td className="py-2 text-gray-500">{row.name}</td>
                <td className="py-2 text-right">{formatReturn(row.return_1m)}</td>
                <td className="py-2 text-right">{formatReturn(row.return_1y)}</td>
                <td className="py-2 pl-4">
                  <Input
                    size="small"
                    value={row.note}
                    placeholder="Lý do chọn (tuỳ chọn)"
                    onChange={e => updateNote(row.product_id, e.target.value)}
                  />
                </td>
                <td className="py-2">
                  <div className="flex items-center justify-end gap-3">
                    <ArrowUpOutlined
                      className={index === 0 ? 'text-gray-300' : 'cursor-pointer'}
                      onClick={() => moveRow(index, -1)}
                    />
                    <ArrowDownOutlined
                      className={index === rows.length - 1 ? 'text-gray-300' : 'cursor-pointer'}
                      onClick={() => moveRow(index, 1)}
                    />
                    <DeleteOutlined
                      style={{ color: '#ff4d4f', cursor: 'pointer' }}
                      onClick={() => removeRow(row.product_id)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!isNew && response?.data?.performance && (
        <div className="my-4 flex flex-wrap gap-6 rounded-md border px-4 py-3 text-sm">
          <span className="text-gray-500">Hiệu suất chủ đề (trung bình, theo dữ liệu đã lưu):</span>
          {[
            ['1D', 'return_1d'],
            ['1T', 'return_1m'],
            ['3T', 'return_3m'],
            ['6T', 'return_6m'],
            ['1N', 'return_1y'],
            ['YTD', 'return_ytd'],
          ].map(([label, key]) => (
            <span key={key}>
              {label}: {formatReturn(response.data.performance[key])}
            </span>
          ))}
        </div>
      )}

      {publishWithoutItems && (
        <p className="my-2 text-sm text-red-500">Cần ít nhất một sản phẩm để xuất bản.</p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <Button type="primary" disabled={!canSave} loading={isSaving} onClick={() => save()}>
          Lưu chủ đề
        </Button>
        {!isNew && (
          <Popconfirm title="Xoá chủ đề này?" onConfirm={() => remove()}>
            <Button danger loading={isDeleting}>
              Xoá
            </Button>
          </Popconfirm>
        )}
      </div>
    </>
  );
};

/* ---------------- Categories ---------------- */

const EMPTY_CATEGORY = { name: '', name_en: '', slug: '', description: '', ordering: 0, is_active: true };

export const InvestmentThemeCategoryListPage = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);

  const { data: response, isLoading, error: fetchError } = useQuery({
    queryKey: [listInvestmentThemeCategoriesApiCall.queryKey],
    queryFn: () => listInvestmentThemeCategoriesApiCall.queryFn(undefined),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [listInvestmentThemeCategoriesApiCall.queryKey] });

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: (category: any) =>
      category.id
        ? updateInvestmentThemeCategoryApiCall.queryFn(category)
        : createInvestmentThemeCategoryApiCall.queryFn(category),
    onSuccess: () => {
      message.success('Đã lưu nhóm chủ đề');
      setEditing(null);
      invalidate();
    },
    onError: err => {
      message.error(err?.message || 'Lưu nhóm chủ đề thất bại');
    },
  });

  const { mutate: remove } = useMutation({
    mutationFn: id => deleteInvestmentThemeCategoryApiCall.queryFn({ id }),
    onSuccess: () => {
      message.success('Đã xoá nhóm chủ đề');
      invalidate();
    },
    onError: err => {
      message.error(err?.message || 'Xoá nhóm chủ đề thất bại');
    },
  });

  if (fetchError) {
    return <ErrorMessage {...fetchError} />;
  }

  const columns = [
    { title: 'Tên', dataIndex: 'name', key: 'name' },
    { title: 'Tên tiếng Anh', dataIndex: 'name_en', key: 'name_en' },
    { title: 'Slug', dataIndex: 'slug', key: 'slug' },
    { title: 'Thứ tự', dataIndex: 'ordering', key: 'ordering', align: 'right' as const },
    {
      title: 'Hiển thị',
      dataIndex: 'is_active',
      key: 'is_active',
      render: value => (value ? <Tag color="green">Hiện</Tag> : <Tag>Ẩn</Tag>),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <a onClick={() => setEditing({ ...record })}>Sửa</a>
          <Popconfirm
            title="Xoá nhóm này? Các chủ đề trong nhóm sẽ được giữ lại, không thuộc nhóm nào."
            onConfirm={() => remove(record.id)}
          >
            <DeleteOutlined style={{ color: '#ff4d4f', cursor: 'pointer' }} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  const setEditingField = (key, value) => setEditing(prev => ({ ...prev, [key]: value }));

  return (
    <>
      <PageTitle
        title="Nhóm chủ đề đầu tư"
        backUrl="/admin/investment-themes"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing({ ...EMPTY_CATEGORY })}>
            Thêm nhóm
          </Button>
        }
      />
      <p className="mb-4 text-gray-500">
        Mỗi nhóm (VD: Ngành, Chiến lược, Cổ tức) là một mục hiển thị trên trang chủ đề.
      </p>
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={response?.data || []}
        pagination={false}
      />

      <Modal
        open={!!editing}
        title={editing?.id ? 'Sửa nhóm chủ đề' : 'Thêm nhóm chủ đề'}
        okText="Lưu"
        cancelText="Huỷ"
        confirmLoading={isSaving}
        okButtonProps={{ disabled: !editing?.name?.trim() }}
        onOk={() => save(editing)}
        onCancel={() => setEditing(null)}
      >
        {editing && (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Tên" className="col-span-2">
              <Input value={editing.name} onChange={e => setEditingField('name', e.target.value)} />
            </Field>
            <Field label="Tên tiếng Anh">
              <Input
                value={editing.name_en}
                onChange={e => setEditingField('name_en', e.target.value)}
              />
            </Field>
            <Field label="Slug (để trống để tự tạo)">
              <Input value={editing.slug} onChange={e => setEditingField('slug', e.target.value)} />
            </Field>
            <Field label="Mô tả" className="col-span-2">
              <Input.TextArea
                rows={2}
                value={editing.description}
                onChange={e => setEditingField('description', e.target.value)}
              />
            </Field>
            <Field label="Thứ tự">
              <InputNumber
                value={editing.ordering}
                onChange={value => setEditingField('ordering', value || 0)}
                style={{ width: '100%' }}
              />
            </Field>
            <Field label="Hiển thị">
              <Switch
                checked={editing.is_active}
                onChange={value => setEditingField('is_active', value)}
              />
            </Field>
          </div>
        )}
      </Modal>
    </>
  );
};
